/** Shared traffic-light colours and helpers. */

export const TRAFFIC = {
  green: { key: 'green', label: 'OK', badge: 'success', dot: 'bg-emerald-500' },
  amber: { key: 'amber', label: 'Review', badge: 'warning', dot: 'bg-amber-500' },
  red: { key: 'red', label: 'Action needed', badge: 'destructive', dot: 'bg-red-500' },
  grey: { key: 'grey', label: 'No data', badge: 'secondary', dot: 'bg-muted-foreground/40' },
}

const PRIORITY = { red: 0, amber: 1, green: 2, grey: 3 }

export function worstStatus(...statuses) {
  let worst = 'grey'
  for (const status of statuses) {
    if ((PRIORITY[status] ?? 3) < (PRIORITY[worst] ?? 3)) {
      worst = status
    }
  }
  return worst
}

/** Normalise a posture issue to `{ text, status }` (strings stay valid). */
export function normaliseIssue(issue, fallbackStatus = 'amber') {
  if (issue == null || issue === '') return null
  if (typeof issue === 'string') {
    return { text: issue, status: fallbackStatus }
  }
  const text = issue.text || issue.issue || issue.message || ''
  if (!text) return null
  const status = issue.status && PRIORITY[issue.status] != null ? issue.status : fallbackStatus
  return { text, status }
}

export function issueText(issue) {
  return normaliseIssue(issue)?.text ?? ''
}

export function issueStatus(issue, fallbackStatus = 'amber') {
  return normaliseIssue(issue, fallbackStatus)?.status ?? fallbackStatus
}

export function sortIssuesBySeverity(issues, fallbackStatus = 'amber') {
  return [...issues]
    .map((issue) => normaliseIssue(issue, fallbackStatus))
    .filter(Boolean)
    .sort((a, b) => (PRIORITY[a.status] ?? 3) - (PRIORITY[b.status] ?? 3))
}

/** Worst of security + hardware posture (ignores grey when other data exists). */
export function combinedDeviceStatus(security, hardware) {
  const active = [security, hardware].filter((status) => status && status !== 'grey')
  if (!active.length) return 'grey'
  return worstStatus(...active)
}

export function buildPosture({ sections, sectionDefs, headlines }) {
  const activeSections = sections.filter((section) => section.status !== 'grey')
  const overallStatus = activeSections.length
    ? worstStatus(...activeSections.map((s) => s.status))
    : 'grey'

  const allIssues = sections.flatMap((s) =>
    s.issues.map((issue) => {
      const normalised = normaliseIssue(issue, s.status)
      return {
        section: s.title,
        issue: normalised?.text ?? issueText(issue),
        status: normalised?.status ?? s.status,
      }
    }),
  )

  const overallTraffic = TRAFFIC[overallStatus] ?? TRAFFIC.grey
  const headline = headlines[overallStatus] ?? headlines.grey

  return {
    overall: {
      status: overallStatus,
      label: overallTraffic.label,
      badge: overallTraffic.badge,
      dot: overallTraffic.dot,
      headline,
      issues: allIssues,
    },
    sections,
    sectionById: Object.fromEntries(sections.map((s) => [s.id, s])),
  }
}

export function mapSectionResults(sectionDefs, evaluators) {
  return sectionDefs.map((section) => {
    const result = evaluators.find((e) => e.id === section.id) ?? {
      id: section.id,
      status: 'grey',
      issues: [],
    }
    const traffic = TRAFFIC[result.status] ?? TRAFFIC.grey
    return {
      ...section,
      status: result.status,
      statusLabel: traffic.label,
      badge: traffic.badge,
      dot: traffic.dot,
      issues: result.issues,
    }
  })
}
